import { calculateRunRate, calculateRequiredHours } from './dos'

const MONTH_KEYS = ['apr26', 'may26', 'jun26', 'jul26', 'aug26', 'sep26']
const MONTH_LABELS = ['April', 'May', 'June', 'July', 'Aug', 'Sept']
const MONTH_ISO = ['2026-04', '2026-05', '2026-06', '2026-07', '2026-08', '2026-09']

const WORKING_DAYS = { apr26: 22, may26: 20, jun26: 22, jul26: 22, aug26: 21, sep26: 21 }

function matchesScope(mod, row) {
  if (mod.scope === 'AllProduct') return true
  if (mod.scope === 'BriteCode') return String(row.briteCode) === String(mod.identifier)
  if (mod.scope === 'ItemNumber') return String(row.itemNumber) === String(mod.identifier)
  return false
}

function getMonthValue(mod, monthKey) {
  const idx = MONTH_KEYS.indexOf(monthKey)
  if (idx < 0) return null

  if (mod.monthValues && typeof mod.monthValues === 'object') {
    const isoKey = MONTH_ISO[idx]
    const labelKey = MONTH_LABELS[idx]

    if (mod.monthValues[isoKey] !== undefined && mod.monthValues[isoKey] !== null) return mod.monthValues[isoKey]
    if (mod.monthValues[labelKey] !== undefined && mod.monthValues[labelKey] !== null) return mod.monthValues[labelKey]
    if (mod.monthValues[monthKey] !== undefined && mod.monthValues[monthKey] !== null) return mod.monthValues[monthKey]
  }

  if (mod.defaultValue !== undefined && mod.defaultValue !== null) return mod.defaultValue
  return null
}

/**
 * Apply modifications to base row data and return modified rows.
 *
 * Modifications apply bottom-up (lowest listPosition first, highest last).
 * Each modification operates on the RESULT of the previous one, not the base.
 * This means stacking order matters.
 *
 * PercentOfMinInv: CASES = previousCases * value (e.g., 1.30 = 130% of previous)
 * Qty: CASES = previousCases + value (can be negative, floor at 0)
 */
export function applyModifications(baseRows, modifications, mode = 'modified') {
  if (mode === 'base' || !modifications || modifications.length === 0) {
    return baseRows
  }

  const activeMods = modifications
    .filter(m => !m.isIgnored)
    .sort((a, b) => (a.listPosition || 0) - (b.listPosition || 0))

  if (activeMods.length === 0) return baseRows

  return baseRows.map(baseRow => {
    const row = { ...baseRow }
    let anyModified = false

    for (const mk of MONTH_KEYS) {
      const casesField = `${mk}_cases`
      const hrsField = `${mk}_hrs`
      const dosField = `${mk}_dos`
      const pctField = `${mk}_pctMinInv`
      const modifiedField = `${mk}_scenarioModified`

      const baseCases = baseRow[casesField] || 0
      let currentCases = baseCases
      let wasModified = false
      let lastPctValue = null

      for (const mod of activeMods) {
        if (!matchesScope(mod, row)) continue

        const value = getMonthValue(mod, mk)
        if (value === null) continue

        if (mod.changeType === 'PercentOfMinInv') {
          currentCases = Math.round(currentCases * value)
          wasModified = true
          lastPctValue = value
        } else if (mod.changeType === 'Qty') {
          currentCases = Math.max(0, currentCases + value)
          wasModified = true
        }
      }

      if (wasModified) {
        row[casesField] = currentCases
        row[modifiedField] = true
        anyModified = true

        if (lastPctValue !== null) {
          row[pctField] = Math.round(lastPctValue * 100)
        }

        const runRate = calculateRunRate(row.cpm || 400, row.efficiency || 0.85, row.packSize || 12)
        row[hrsField] = Math.round(calculateRequiredHours(currentCases, runRate) * 10) / 10

        const fcst = row[`${mk}_fcst`] || 0
        if (fcst > 0) {
          const nextIdx = MONTH_KEYS.indexOf(mk) + 1
          const nextFcst = nextIdx < MONTH_KEYS.length ? (row[`${MONTH_KEYS[nextIdx]}_fcst`] || fcst) : fcst
          const nextWd = nextIdx < MONTH_KEYS.length ? (WORKING_DAYS[MONTH_KEYS[nextIdx]] || 22) : 22
          const nextDailyRate = nextFcst / nextWd

          const beginInv = baseRow[`${mk}_beginInv`] || baseCases * 3
          const endInv = beginInv - fcst + currentCases
          if (nextDailyRate > 0) {
            row[dosField] = Math.round((endInv / nextDailyRate) * 10) / 10
          }
        }
      }
    }

    if (anyModified) {
      row.hasIssue = MONTH_KEYS.some(mk => {
        const dos = row[`${mk}_dos`]
        return dos !== undefined && dos !== null && dos < row.dosMin
      })
    }

    return row
  })
}
